import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import {
  CustomFieldFilter,
  PendingInboxItem,
  PendingInboxItemsResponseSchema,
  InboxScope,
} from "@forgepoint/shared";

interface LoadingState {
  isLoadingGet: boolean;
  getError: string | null;
}

let latestInboxRequestId = 0;

const getDefaultLoadingState = (): LoadingState => ({
  isLoadingGet: false,
  getError: null,
});

interface AgentTasksStore {
  itemMap: Map<string, PendingInboxItem>;
  itemIds: string[];
  loadingState: LoadingState;
  viewMode: InboxScope;
  filterUserId: string | null;
  filterOpportunityId: string | null;
  filterCustomFields: CustomFieldFilter;
  fetchInboxItems: () => Promise<void>;
  setViewMode: (mode: InboxScope) => void;
  setFilterUserId: (userId: string | null) => void;
  setFilterOpportunityId: (opportunityId: string | null) => void;
  setFilterCustomFields: (customFields: CustomFieldFilter) => void;
  clearFilters: () => void;
  removeInboxItem: (taskId: string) => PendingInboxItem | null;
  restoreInboxItem: (item: PendingInboxItem) => void;
  cancelTask: (
    taskId: string,
  ) => Promise<{ ok: boolean; error: string | null }>;
}

export const useAgentTasksStore = create<AgentTasksStore>((set, get) => ({
  itemMap: new Map(),
  itemIds: [],
  loadingState: getDefaultLoadingState(),
  viewMode: "USER",
  filterUserId: null,
  filterOpportunityId: null,
  filterCustomFields: {},

  /**
   * Switches between the personal and organization inbox and refetches accordingly.
   * @param mode - The inbox view to switch to
   */
  setViewMode: (mode) => {
    set({ viewMode: mode });
    get().fetchInboxItems();
  },

  /**
   * Sets the organization-inbox assignee filter and refetches.
   * @param userId - Assignee user id to filter by, or null to clear
   */
  setFilterUserId: (userId) => {
    set({ filterUserId: userId });
    get().fetchInboxItems();
  },

  /**
   * Sets the organization-inbox opportunity filter and refetches.
   * @param opportunityId - Opportunity id to filter by, or null to clear
   */
  setFilterOpportunityId: (opportunityId) => {
    set({ filterOpportunityId: opportunityId });
    get().fetchInboxItems();
  },

  /**
   * Sets the opportunity custom field filter and refetches.
   * @param customFields - Field name to accepted values; empty to clear
   */
  setFilterCustomFields: (customFields) => {
    set({ filterCustomFields: customFields });
    get().fetchInboxItems();
  },

  /**
   * Clears the assignee, opportunity, and custom field filters and refetches once.
   */
  clearFilters: () => {
    set({
      filterUserId: null,
      filterOpportunityId: null,
      filterCustomFields: {},
    });
    get().fetchInboxItems();
  },

  fetchInboxItems: async () => {
    const requestId = ++latestInboxRequestId;
    set({
      loadingState: {
        isLoadingGet: true,
        getError: null,
      },
    });

    try {
      const {
        viewMode,
        filterUserId,
        filterOpportunityId,
        filterCustomFields,
      } = get();
      const params: Record<string, string> = {};
      if (viewMode === "ORGANIZATION") {
        params.scope = "ORGANIZATION";
        if (filterUserId) {
          params.assigneeUserId = filterUserId;
        }
      }
      if (filterOpportunityId) {
        params.opportunityId = filterOpportunityId;
      }
      if (Object.keys(filterCustomFields).length > 0) {
        params.customFields = JSON.stringify(filterCustomFields);
      }
      const response = await fetchApi("tasks/inbox-items", "GET", {
        params: Object.keys(params).length > 0 ? params : undefined,
      });
      if (requestId !== latestInboxRequestId) {
        return;
      }
      const parsed = PendingInboxItemsResponseSchema.safeParse(
        response.ok ? response.data : null,
      );

      if (!parsed.success) {
        captureException(parsed.error);
        set({
          loadingState: {
            isLoadingGet: false,
            getError: "Failed to fetch inbox items",
          },
        });
        return;
      }

      const nextItemMap = new Map<string, PendingInboxItem>();
      const nextItemIds: string[] = [];

      parsed.data.items.forEach((item) => {
        nextItemMap.set(item.taskId, item);
        nextItemIds.push(item.taskId);
      });

      set({
        itemMap: nextItemMap,
        itemIds: nextItemIds,
        loadingState: getDefaultLoadingState(),
      });
    } catch (error) {
      if (requestId !== latestInboxRequestId) {
        return;
      }
      captureException(
        error instanceof Error
          ? error
          : new Error("Failed to fetch inbox items"),
      );
      set({
        loadingState: {
          isLoadingGet: false,
          getError: "Failed to fetch inbox items",
        },
      });
    }
  },

  removeInboxItem: (taskId) => {
    const { itemMap, itemIds } = get();
    const existing = itemMap.get(taskId);
    if (!existing) {
      return null;
    }
    const nextItemMap = new Map(itemMap);
    nextItemMap.delete(taskId);
    set({
      itemMap: nextItemMap,
      itemIds: itemIds.filter((id) => id !== taskId),
    });
    return existing;
  },

  restoreInboxItem: (item) => {
    const { itemMap, itemIds } = get();
    if (itemMap.has(item.taskId)) {
      return;
    }
    const nextItemMap = new Map(itemMap);
    nextItemMap.set(item.taskId, item);
    set({
      itemMap: nextItemMap,
      itemIds: [...itemIds, item.taskId],
    });
  },

  cancelTask: async (taskId) => {
    try {
      const response = await fetchApi(`tasks/${taskId}/deny`, "POST");
      if (!response.ok) {
        return {
          ok: false,
          error: response.error ?? "Failed to cancel task",
        };
      }
      return { ok: true, error: null };
    } catch (error) {
      captureException(
        error instanceof Error ? error : new Error("Failed to cancel task"),
      );
      return {
        ok: false,
        error: error instanceof Error ? error.message : "Failed to cancel task",
      };
    }
  },
}));
